/**
 * Stock documents of Phase 2 step W3 (plan §3, §7, §11, §12; D264–D274): waste, opening stock and
 * their corrections. Shared by the API and the screens, so a field is refused with the same words
 * in both (D93). Quantities are whole numbers of the item's stock unit (g, ml, pieces — D226);
 * screens read what a person types with `parseQuantity` first.
 */
import { z } from 'zod';

import { ATTACHMENT_TYPES, MAX_ATTACHMENT_BYTES } from '../attachments/attachments.ts';
import { isUuidV7 } from '../ids/ids.ts';

/** Every kind of stock movement (plan §3). W3 posts OPENING, WASTE, REVERSAL and COST_ADJUSTMENT. */
export const STOCK_MOVEMENT_KINDS = [
  'OPENING',
  'PURCHASE_RECEIPT',
  'SUPPLIER_RETURN',
  'PRODUCTION_INPUT',
  'PRODUCTION_OUTPUT',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'TRANSIT_FOUND',
  'BRANCH_RETURN_OUT',
  'BRANCH_RETURN_IN',
  'WASTE',
  'COUNT_ADJUSTMENT',
  'CONSUMPTION',
  'COST_ADJUSTMENT',
  'BATCH_RECLASS',
  'REVERSAL',
] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

/** The documents that move stock in W3; each is also its journal entries' source type. */
export const STOCK_DOCUMENT_TYPES = ['waste', 'inventory.opening', 'stock_correction'] as const;
export type StockDocumentType = (typeof STOCK_DOCUMENT_TYPES)[number];

/** Documents a partner can correct (plan §12.1): reversed, then replaced (waste) or prepared again. */
export const CORRECTABLE_DOCUMENT_TYPES = ['waste', 'inventory.opening'] as const;
export type CorrectableDocumentType = (typeof CORRECTABLE_DOCUMENT_TYPES)[number];

export const WASTE_REASONS = ['EXPIRED', 'DAMAGED', 'SPOILED', 'OTHER'] as const;
export type WasteReason = (typeof WASTE_REASONS)[number];

export const WASTE_MAX_LINES = 50;
export const WASTE_MAX_PHOTOS = 10;
export const OPENING_MAX_LINES = 500;
export const STOCK_NOTE_MAX = 500;
export const WASTE_REASON_TEXT_MAX = 300;
export const BATCH_CODE_MAX = 40;
/** A correction's reason: the approval engine asks for at least 10 characters (plan §12.1). */
export const CORRECTION_REASON_MIN = 10;

/** The largest whole quantity or amount the database and JavaScript both keep exactly. */
const SAFE = Number.MAX_SAFE_INTEGER;

const quantitySchema = z.number().int().positive().max(SAFE);
const isoDate = z.iso.date();

export const WasteLineSchema = z.strictObject({
  itemId: z.uuid(),
  /** Required for an item with batches; absent for one without. */
  batchId: z.uuid().nullable().default(null),
  quantity: quantitySchema,
});
export type WasteLine = z.infer<typeof WasteLineSchema>;

/** What a waste says, online or sent later from a device (the photos differ). */
const wasteContentFields = {
  reason: z.enum(WASTE_REASONS),
  /** Required when the reason is OTHER. */
  reasonText: z.string().trim().min(3).max(WASTE_REASON_TEXT_MAX).optional(),
  note: z.string().trim().max(STOCK_NOTE_MAX).optional(),
  lines: z.array(WasteLineSchema).min(1).max(WASTE_MAX_LINES),
};

const wasteFields = {
  ...wasteContentFields,
  /** At least one WASTE photo of this location, already uploaded (W2). */
  attachmentIds: z.array(z.uuid()).min(1).max(WASTE_MAX_PHOTOS),
};

const otherNeedsText = (w: { reason: WasteReason; reasonText?: string | undefined }) =>
  w.reason !== 'OTHER' || (w.reasonText !== undefined && w.reasonText !== '');

export const WasteRecordSchema = z
  .strictObject({
    /** Chosen by the screen (UUIDv7): a retry after a lost answer records nothing twice. */
    id: z.uuid(),
    locationId: z.uuid(),
    ...wasteFields,
  })
  .refine(otherNeedsText, {
    path: ['reasonText'],
    message: 'required',
    params: { dobCode: 'required' },
  });
export type WasteRecord = z.infer<typeof WasteRecordSchema>;
export type WasteRecordInput = z.input<typeof WasteRecordSchema>;

/**
 * A waste recorded on a PC or a phone, sent through the outbox when there is internet (W4; plan
 * §7, §13, §14.8). The same fields as online, but the photos travel as their declarations (id,
 * type, size, SHA-256): the command declares them on the server, so the document is accepted while
 * a photo is still on its way, and the server then takes only those exact bytes for each id.
 */
export const WASTE_RECORD_COMMAND = 'waste.record';

export const WastePhotoSchema = z.strictObject({
  id: z.uuid().refine(isUuidV7, { message: 'UUIDv7 expected' }),
  contentType: z.enum(ATTACHMENT_TYPES),
  byteSize: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type WastePhoto = z.infer<typeof WastePhotoSchema>;

export const WasteRecordCommandSchema = z
  .strictObject({
    /** The waste's id, chosen by the device (UUIDv7): sent again, it is recorded once. */
    id: z.uuid(),
    /** The command's own location: a PC records waste only where it stands. */
    locationId: z.uuid(),
    ...wasteContentFields,
    photos: z
      .array(WastePhotoSchema)
      .min(1)
      .max(WASTE_MAX_PHOTOS)
      .refine((photos) => new Set(photos.map((p) => p.id)).size === photos.length, {
        message: 'A photo appears twice',
      }),
  })
  .refine(otherNeedsText, {
    path: ['reasonText'],
    message: 'required',
    params: { dobCode: 'required' },
  });
export type WasteRecordCommand = z.infer<typeof WasteRecordCommandSchema>;
export type WasteRecordCommandInput = z.input<typeof WasteRecordCommandSchema>;

/**
 * A waste typed on a Dob Desktop PC, as its page hands it to the main process (W4-7, D289): the
 * same fields, with the photos as their bytes (already shrunk by the page). The main process makes
 * their declarations itself (id, real type, size, SHA-256) and saves the waste and the photos in
 * one transaction; the location is always the PC's own.
 */
export const DesktopWasteInputSchema = z
  .strictObject({
    /** Chosen by the page, once per form: the same waste sent again is saved once. */
    id: z.uuid().refine(isUuidV7, { message: 'UUIDv7 expected' }),
    ...wasteContentFields,
    photos: z
      .array(
        z
          .instanceof(Uint8Array)
          .refine((b) => b.byteLength >= 1 && b.byteLength <= MAX_ATTACHMENT_BYTES, {
            message: 'A photo is 1 byte to 10 MiB',
          }),
      )
      .min(1)
      .max(WASTE_MAX_PHOTOS),
  })
  .refine(otherNeedsText, {
    path: ['reasonText'],
    message: 'required',
    params: { dobCode: 'required' },
  });
export type DesktopWasteInput = z.input<typeof DesktopWasteInputSchema>;

/** The new, correct waste that replaces a corrected one (same location; new id made at posting). */
export const WasteReplacementSchema = z.strictObject(wasteFields).refine(otherNeedsText, {
  path: ['reasonText'],
  message: 'required',
  params: { dobCode: 'required' },
});
export type WasteReplacement = z.infer<typeof WasteReplacementSchema>;

export const OpeningBatchSchema = z
  .strictObject({
    code: z.string().trim().min(1).max(BATCH_CODE_MAX),
    productionDate: isoDate.nullable().default(null),
    expiryDate: isoDate,
  })
  .refine((b) => b.productionDate === null || b.productionDate <= b.expiryDate, {
    path: ['productionDate'],
    message: 'production_after_expiry',
    params: { dobCode: 'production_after_expiry' },
  });

export const OpeningLineSchema = z.strictObject({
  itemId: z.uuid(),
  quantity: quantitySchema,
  /** Dinars for every `costPer` stock units (per piece = 1, per kg or per L = 1,000). */
  unitCost: z.number().int().min(0).max(SAFE),
  costPer: z.number().int().min(1).max(1_000_000),
  /** Required for an item with batches (its code and expiry date); absent for one without. */
  batch: OpeningBatchSchema.nullable().default(null),
});
export type OpeningLine = z.infer<typeof OpeningLineSchema>;

export const OpeningPrepareSchema = z.strictObject({
  id: z.uuid(),
  locationId: z.uuid(),
  /** The day the counted stock was there; today when omitted. Never in the future. */
  businessDate: isoDate.optional(),
  note: z.string().trim().max(STOCK_NOTE_MAX).optional(),
  lines: z.array(OpeningLineSchema).min(1).max(OPENING_MAX_LINES),
});
export type OpeningPrepare = z.infer<typeof OpeningPrepareSchema>;
export type OpeningPrepareInput = z.input<typeof OpeningPrepareSchema>;

export const OpeningCancelSchema = z.strictObject({ reason: z.string().trim().min(5).max(500) });

/** Payload of the approval `inventory.opening.post`. */
export const OpeningPostPayloadSchema = z.strictObject({ documentId: z.uuid() });

/** Payload of the approval `inventory.document.correct` (plan §12.1). */
export const StockCorrectionPayloadSchema = z
  .strictObject({
    documentType: z.enum(CORRECTABLE_DOCUMENT_TYPES),
    documentId: z.uuid(),
    /** Waste only: the correct waste that replaces it. Omitted: the document is only reversed. */
    replacement: WasteReplacementSchema.optional(),
  })
  .refine((p) => p.documentType === 'waste' || p.replacement === undefined, {
    path: ['replacement'],
    message: 'A reversed opening is replaced by preparing a new one',
  });
export type StockCorrectionPayload = z.infer<typeof StockCorrectionPayloadSchema>;

/** The automatic match's checks (plan §17; migration 0022 `stock_reconciliation`). */
export const RECONCILIATION_CHECKS = [
  'STOCK_VS_LEDGER',
  'WRONG_INVENTORY_ACCOUNT',
  'TRANSIT_NOT_EMPTY',
  'REVIEW_NOT_EMPTY',
  'BALANCE_QUANTITY',
  'BALANCE_VALUE',
  'BALANCE_MISSING',
  'BATCH_QUANTITY',
  'PARTY_UNNAMED',
] as const;
export type ReconciliationCheck = (typeof RECONCILIATION_CHECKS)[number];

/** The stock units an opening line's cost is usually typed per: a piece, a kg or a litre. */
export const costPerDisplayUnit = (stockUnit: 'G' | 'ML' | 'PC'): number =>
  stockUnit === 'PC' ? 1 : 1000;
